import { useState } from 'react';
import { Badge } from '../../components/ui';
import { SignaturePad } from '../../components/phase3';
import type { SignatureValue } from '../../components/phase3';
import { TextField } from '../../components/ui';
import { t } from '../../lib/i18n';
import type { PermitStatusKey } from './api';

const TONE: Record<PermitStatusKey, 'neutral' | 'good' | 'warn' | 'bad'> = {
  requested: 'warn',
  issued: 'neutral',
  active: 'good',
  suspended: 'warn',
  closed: 'neutral',
  cancelled: 'neutral',
  expired: 'bad',
};

/** The status as a coloured word (the word carries the meaning, not the colour). */
export function PermitStatusBadge({ status }: { status: PermitStatusKey }) {
  return <Badge tone={TONE[status]}>{t(`p3pt.status.${status}`)}</Badge>;
}

export type SignOff = { role: string; name: string; strokes: SignatureValue };

/**
 * One sign-off: the role, a printed name, and the drawn signature (W16 pad). The server stores only drawn strokes
 * (permits.md s2), so there is no typed-only route; the printed name and the Clear / Undo buttons are keyboard reachable.
 */
export function SignOffField({
  role,
  defaultName = '',
  disabled,
  onChange,
}: {
  role: string;
  defaultName?: string;
  disabled?: boolean;
  onChange: (value: SignOff | null) => void;
}) {
  const [name, setName] = useState(defaultName);
  const [strokes, setStrokes] = useState<SignatureValue | null>(null);
  const emit = (n: string, s: SignatureValue | null) => onChange(n.trim() !== '' && s ? { role, name: n.trim(), strokes: s } : null);
  const label = t(`p3pt.role.${role}`);
  return (
    <fieldset className="space-y-3 rounded-lg border border-slate-200 p-3">
      <legend className="px-1 text-sm font-semibold text-slate-800">{label}</legend>
      <TextField
        label={t('p3pt.signoff.name', { role: label })}
        value={name}
        disabled={disabled}
        autoComplete="off"
        onChange={(e) => {
          setName(e.target.value);
          emit(e.target.value, strokes);
        }}
      />
      <SignaturePad
        label={t('p3pt.signoff.pad', { role: label })}
        disabled={disabled ?? false}
        onChange={(v) => {
          setStrokes(v);
          emit(name, v);
        }}
      />
    </fieldset>
  );
}

export function KeyValue({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-semibold text-slate-600">{label}</dt>
      <dd className="mt-0.5 text-sm text-slate-900">{children}</dd>
    </div>
  );
}
